import React, { useCallback, useEffect, useState } from 'react';
import { Dropdown, Button, Avatar, message, Tag } from 'antd';
import { TeamOutlined, DownOutlined, PlusOutlined, SwapOutlined, CrownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';

// Switcher de time no header do organizador. So aparece se >1 times.
// Clique num item -> POST /switch -> substitui JWT -> reload.
export default function TeamSwitcher() {
  const nav = useNavigate();
  const [teams, setTeams] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [canCreate, setCanCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/organizer/teams/my');
      setTeams(r.data.teams || []);
      setActiveId(r.data.active_customer_id);
      setCanCreate(!!r.data.can_create_new_team);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const switchTo = async (customerId) => {
    if (customerId === activeId) return;
    try {
      const r = await api.post('/organizer/teams/switch', { customer_id: customerId });
      localStorage.setItem('token', r.data.token);
      localStorage.setItem('user', JSON.stringify(r.data.user));
      // Grava tambem active_customer_id — interceptor da api injeta como
      // header x-active-customer-id, backend prefere sobre o JWT (cobre
      // token stale em cenarios de multi-tab / cache).
      localStorage.setItem('active_customer_id', String(customerId));
      if (r.data.user?.game_name) localStorage.setItem('active_customer_name', r.data.user.game_name);
      message.success(`Entrou em ${r.data.user.game_name}`);
      // Preserva rota atual — reload da mesma tela pega dados do time novo
      // via x-active-customer-id header. So cai pra /team-config se for
      // rota-raiz (ex: /organizer/teams) ou se falhar o match.
      const path = window.location.pathname;
      const isNeutral = path === '/' || path === '/organizer/teams' || path.startsWith('/organizer/teams');
      if (isNeutral) {
        window.location.href = '/team-config';
      } else {
        window.location.reload();
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'Falhou');
    }
  };

  // Regras de exibicao:
  // - 0 times: nao renderiza (sessao meio quebrada)
  // - 1 time + !can_create: nao renderiza (comportamento antigo, zero ruido)
  // - 1 time + can_create: mostra so o botao "Criar novo time" (sem dropdown)
  // - >1 times: mostra dropdown com switch e opcao criar (se can_create)
  if (teams.length === 0) return null;
  if (teams.length === 1 && !canCreate) return null;

  if (teams.length === 1 && canCreate) {
    return (
      <Button size="small" icon={<PlusOutlined />} onClick={() => nav('/organizer/teams/new')}>
        Criar novo time
      </Button>
    );
  }

  const active = teams.find(t => t.id === activeId);

  const items = teams.map(t => ({
    key: `t-${t.id}`,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
        <Avatar size="small" src={absoluteUploadUrl(t.logo_url)} icon={<TeamOutlined />} />
        <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <b>{t.name}</b>
          {t.role === 'OWNER' && <CrownOutlined style={{ color: '#faad14', marginLeft: 6 }} />}
          {t.status !== 'ATIVO' && <Tag color="red" style={{ marginLeft: 6 }}>{t.status}</Tag>}
        </div>
        {t.id === activeId && <Tag color="green" style={{ marginRight: 0 }}>atual</Tag>}
      </div>
    ),
    onClick: () => switchTo(t.id),
  }));

  if (canCreate) {
    items.push({ type: 'divider' });
    items.push({
      key: 'new',
      label: <span><PlusOutlined /> Criar novo time</span>,
      onClick: () => nav('/organizer/teams/new'),
    });
  }

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button size="small" icon={<SwapOutlined />}>
        {active?.name?.substring(0, 20) || 'Time'} <DownOutlined />
      </Button>
    </Dropdown>
  );
}
